import React, { Dispatch, SetStateAction, useState } from "react";

import {
  canCancelFieldReactivation,
  canDeleteField,
  isAutoEnrolledInventoryProgram,
  useEnrollField,
  useGetFarmReportingYear,
  useGetFarms,
  useTranslation,
} from "utils";

import { CarbonProgram, FieldDetails as FieldDetailsType, FieldPracticeResponse, PRACTICE_SLUG } from "models";

import { CancelFieldReactivationModal, ContextMenu, ContextMenuItemProps, Flex, Icon, IconButton, Typography } from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

type SoilPlus3MenuMenuProps = {
  availablePracticeBundles: FieldPracticeResponse["availablePracticeBundles"];
  isEnrolled: boolean;
  setShowFieldEditDrawer: Dispatch<SetStateAction<boolean>>;
  setShowDeleteFieldModal: Dispatch<SetStateAction<boolean>>;
  carbonProgram?: CarbonProgram | null;
  isPracticeEnrollmentDisabled?: boolean;
  hectareLimitWouldBeExceeded: boolean;
  field: FieldDetailsType;
  placement?: "right" | "left" | "bottom-left" | "bottom-right";
  onAddCrop?: () => void;
  // when set, "Add crop" shows disabled with this text instead of being removed or clickable — e.g. the
  // selected year falls outside the range crops can actually be added for
  addCropDisabledReason?: string;
};

type FieldEnrollmentDetailsReason = "hectareLimitExceeded" | "payoutUnavailable";

const FieldEnrollmentDetails = ({ reason }: { reason: FieldEnrollmentDetailsReason }) => {
  const { t } = useTranslation(["farm", "allFieldView"]);

  if (reason === "hectareLimitExceeded") {
    return (
      <Flex vertical gap="0.25rem">
        <Typography variant="paragraph-s" bold>
          {t("farm:contextMenu.hectareLimitIntro")}
        </Typography>
        <Typography variant="paragraph-s">{t("farm:contextMenu.hectareLimitHelperText")}</Typography>
      </Flex>
    );
  }
  return (
    <Typography variant="paragraph-s" className="margin-0">
      {t("allFieldView:fieldCell.tooltipPayout")}
    </Typography>
  );
};

export const SoilPlus3Menu = ({
  availablePracticeBundles,
  field,
  isEnrolled,
  setShowFieldEditDrawer,
  setShowDeleteFieldModal,
  carbonProgram,
  isPracticeEnrollmentDisabled,
  hectareLimitWouldBeExceeded,
  placement = "right",
  onAddCrop,
  addCropDisabledReason,
}: SoilPlus3MenuMenuProps) => {
  const { t } = useTranslation(["farm", "onboarding", "fieldTimeline", "allFieldView"]);

  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const [showCancelReactivationModal, setShowCancelReactivationModal] = useState<boolean>(false);

  const { currentFarm } = useGetFarms();

  const mineralFertilizerBundle = availablePracticeBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS);
  const { handleEnrollField } = useEnrollField({
    fieldId: field?.id,
    farmId: currentFarm?.id,
    isMineralFertilizerActive: mineralFertilizerBundle?.active,
  });

  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isDataSubmitted: isDataSubmittedForCurrentReportingYear, isDataSubmissionReopened } = useSubmissionStatus(activeFarmReportingYear);
  const isEnrollmentLockedBySubmission = isDataSubmittedForCurrentReportingYear && !isDataSubmissionReopened;

  const requiredPractices = carbonProgram?.fieldsManagement.practices?.filter((practice) => practice.required) ?? [];
  const bundleHasConfirmedData = (bundleSlug: string) => !!availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.hasConfirmedData;
  const bundleIsDiscontinued = (bundleSlug: string) =>
    Number.isInteger(availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.discontinuedYear);
  const hasAnyRequiredPracticesWithConfirmedData = requiredPractices.some((practice) => bundleHasConfirmedData(practice.slug));
  const hasAnyDiscontinuedRequiredPractices = requiredPractices.some((practice) => bundleIsDiscontinued(practice.slug));

  const fieldEnrollmentCheckboxState =
    isPracticeEnrollmentDisabled || hasAnyDiscontinuedRequiredPractices || hasAnyRequiredPracticesWithConfirmedData || isEnrollmentLockedBySubmission
      ? ("disabled" as ContextMenuItemProps["state"])
      : undefined;
  const getEnrollmentTooltip = () => {
    if (hasAnyDiscontinuedRequiredPractices) return t("onboarding:fieldDetails.carbonProgram.enrollmentDisabledRejected");
    if (hasAnyRequiredPracticesWithConfirmedData) return t("onboarding:fieldDetails.carbonProgram.expectedPayoutTooltipVerified");
    if (isEnrollmentLockedBySubmission) return t("onboarding:fieldDetails.carbonProgram.enrollmentDisabledDataSubmitted");
    return undefined;
  };
  const enrollmentTooltip = getEnrollmentTooltip();
  const enrollmentDetailsReason: FieldEnrollmentDetailsReason | undefined = hectareLimitWouldBeExceeded
    ? "hectareLimitExceeded"
    : isPracticeEnrollmentDisabled && !carbonProgram
    ? "payoutUnavailable"
    : undefined;

  const isFieldDeletable = canDeleteField({
    availablePracticeBundles,
    carbonProgram,
  });
  const isReactivationCancellable = canCancelFieldReactivation(availablePracticeBundles, activeFarmReportingYear);

  return (
    <>
      <ContextMenu
        className="field-timeline"
        dataTestId="field-timeline-context-menu"
        items={[
          ...(isAutoEnrolledInventoryProgram(carbonProgram)
            ? []
            : [
                {
                  hasToggle: true,
                  iconName: "tabler/layout-grid-add",
                  isToggled: isEnrolled,
                  label: isEnrolled ? (
                    <Flex align="center" gap="0.25rem">
                      <Typography variant="paragraph-m">{t("farm:contextMenu.fieldEnrolled")}</Typography>
                      <Icon iconName="tabler/info-circle" className="icon inline" size="s" />
                    </Flex>
                  ) : (
                    <Flex vertical={true} align="flex-start" justify="center" gap="0.25rem">
                      <Typography variant="paragraph-m" className="margin-0">
                        {t("farm:contextMenu.enrollFieldInProgram")}
                      </Typography>
                      <Typography variant="paragraph-s" className="margin-0">
                        {t("farm:contextMenu.enrollFieldInProgramHelperText")}
                      </Typography>
                    </Flex>
                  ),
                  details: enrollmentDetailsReason ? <FieldEnrollmentDetails reason={enrollmentDetailsReason} /> : undefined,
                  state: fieldEnrollmentCheckboxState,
                  tooltip: enrollmentTooltip,
                  onToggle: async (value: boolean) => {
                    await handleEnrollField(value);
                    setShowContextMenu(false);
                  },
                  className: hasAnyDiscontinuedRequiredPractices ? "discontinued" : undefined,
                },
              ]),
          ...(onAddCrop
            ? [
                {
                  iconName: "klim/plus",
                  label: t("farm:contextMenu.addCrop"),
                  onClick: addCropDisabledReason ? undefined : onAddCrop,
                  state: addCropDisabledReason ? ("disabled" as ContextMenuItemProps["state"]) : undefined,
                  details: addCropDisabledReason ? (
                    <Typography variant="paragraph-s" className="margin-0">
                      {addCropDisabledReason}
                    </Typography>
                  ) : undefined,
                },
              ]
            : []),
          {
            iconName: "tabler/edit",
            label: t("farm:contextMenu.editFieldDetails"),
            onClick: () => setShowFieldEditDrawer(true),
          },
          ...(isReactivationCancellable
            ? [
                {
                  iconName: "tabler/circle-x",
                  label: t("farm:contextMenu.cancelReactivation"),
                  onClick: () => {
                    setShowContextMenu(false);
                    setShowCancelReactivationModal(true);
                  },
                  state: "warning" as ContextMenuItemProps["state"],
                },
              ]
            : []),
          {
            iconName: "tabler/trash",
            label: t("farm:contextMenu.deleteField"),
            onClick: () => setShowDeleteFieldModal?.(true),
            state: isFieldDeletable ? ("warning" as ContextMenuItemProps["state"]) : "disabled",
          },
        ]}
        placement={placement}
        iconSize="m-light"
        visible={showContextMenu}
        onClose={() => setShowContextMenu(false)}
      >
        <div
          className="context-menu-click-target"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowContextMenu(true);
          }}
        >
          <IconButton
            className="header-dots-vertical-icon"
            iconName="tabler/dots-vertical"
            size="small"
            type="tertiary"
            variant="compact"
            tooltip={{ title: t("allFieldView:fieldContextMenu.editField") }}
            strokeWidth={2}
            dataTestId="dots-vertical-icon"
            onClick={() => setShowContextMenu(!showContextMenu)}
          />
        </div>
        {isReactivationCancellable && (
          <div onClick={(e) => e.stopPropagation()}>
            <CancelFieldReactivationModal
              fieldId={field.id}
              fieldName={field.name}
              visible={showCancelReactivationModal}
              onClose={() => setShowCancelReactivationModal(false)}
            />
          </div>
        )}
      </ContextMenu>
    </>
  );
};
